import { FunctionComponent, useEffect, useRef } from 'react'

import styled, { css } from 'styled-components'

import { PlayerProgress } from 'audioContext'
import { usePlayerSeek } from 'hooks/usePlayerSeek'
import { color } from 'styles/global'
import { defaultTheme } from 'styles/theme'
import { space } from 'styles/utils'

export function drawLine(
  ctx: CanvasRenderingContext2D,
  info: { x: number; y: number; toX: number; toY: number; thickness?: number }
) {
  const { x, y, toX, toY, thickness } = info

  ctx.beginPath()
  ctx.moveTo(x + 0.5, y)
  ctx.lineTo(toX + 0.5, toY)
  ctx.lineWidth = thickness || 1
  ctx.stroke()
  ctx.closePath()
}

export type SeekBarProps = {
  loaded: boolean
  progress?: PlayerProgress
  onSeek?(percent: number): void
}

export const SeekBarContainer = styled.div<{
  canSeek?: boolean
  isSeeking?: boolean
}>`
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
  height: 26px;
  position: relative;
  width: 100%;
  z-index: 0;

  ${({ canSeek }) =>
    canSeek &&
    css`
      &:after {
        background: ${color('interactive')};
        border-radius: 50%;
        content: ' ';
        display: block;
        height: ${space(3)};
        left: calc(var(--seek-position) - 2px);
        pointer-events: none;
        position: absolute;
        top: 8px;
        width: ${space(3)};
        z-index: 23;
      }
    `}

  ${({ canSeek, isSeeking }) =>
    canSeek &&
    isSeeking &&
    css`
      &:after {
        height: ${space(4)};
        left: calc(var(--seek-position) - 6px);
        top: 5px;
        width: ${space(4)};
      }
    `}
`

export const SeekBar: FunctionComponent<SeekBarProps> = ({
  loaded,
  progress = {
    current: 0,
    remaining: 0,
    percent: 0,
  },
  onSeek,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const canSeek = loaded

  const { isSeeking, seekProgress, ...seekProps } = usePlayerSeek(
    canvasRef,
    canSeek,
    onSeek
  )

  const getCurrentProgress = () => {
    let finalProgress = 0
    if (isSeeking) {
      finalProgress = seekProgress
    } else {
      finalProgress = progress.percent / 100
    }

    // Progress can be NaN if since now we load the player
    // before there's any media ready
    if (finalProgress < 0 || Number.isNaN(finalProgress)) {
      return 0
    }

    if (finalProgress > 1) {
      return 1
    }

    return finalProgress
  }

  const getProgressGradient = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ) => {
    const gradient = ctx.createLinearGradient(0, 0, width, height)
    // Make sure the progress is never less than 0
    // or more than 100
    const percent = getCurrentProgress()
    gradient.addColorStop(0, defaultTheme.color.interactiveHover.string())
    gradient.addColorStop(percent, defaultTheme.color.interactiveHover.string())
    gradient.addColorStop(percent, defaultTheme.color.interactive.string())
    gradient.addColorStop(1, defaultTheme.color.interactive.string())
    return gradient
  }

  const draw = (ctx: CanvasRenderingContext2D) => {
    const { clientHeight, clientWidth } = ctx.canvas

    if (wrapperRef.current) {
      const percent = getCurrentProgress()
      wrapperRef.current.style.setProperty(
        '--seek-position',
        `${clientWidth * percent}px`
      )
    }

    // height minus the space for the handle
    const height = clientHeight - 4
    const width = clientWidth

    ctx.clearRect(0, 0, width, clientHeight)

    ctx.strokeStyle = getProgressGradient(ctx, width, height)

    // horizontal line
    drawLine(ctx, { x: 0, y: height, toX: width, toY: height, thickness: 2 })
  }

  const onWindowResize = () => {
    const { current } = canvasRef
    if (current) {
      current.width = wrapperRef.current?.clientWidth || 0
      current.height = (wrapperRef.current?.clientHeight || 0) / 2
      const context = current.getContext('2d')
      if (context) draw(context)
    }
  }

  useEffect(() => {
    onWindowResize()
    window.addEventListener('resize', onWindowResize, false)
    window.addEventListener('orientationchange', onWindowResize, false)
    return () => {
      window.removeEventListener('resize', onWindowResize, false)
      window.removeEventListener('orientationchange', onWindowResize, false)
    }
  }, [])

  useEffect(() => {
    const { current } = canvasRef

    if (!current) {
      return undefined
    }

    current.width = wrapperRef.current?.clientWidth || 0
    current.height = (wrapperRef.current?.clientHeight || 0) / 2

    const context = current.getContext('2d')

    if (!context) {
      return undefined
    }

    context.imageSmoothingEnabled = false

    const frameHandle = window.requestAnimationFrame(() => draw(context))

    return () => window.cancelAnimationFrame(frameHandle)
  }, [canvasRef, wrapperRef, progress, seekProgress, isSeeking, loaded])

  return (
    <SeekBarContainer
      ref={wrapperRef}
      canSeek={canSeek}
      isSeeking={isSeeking}
      {...seekProps}
    >
      <canvas ref={canvasRef} />
    </SeekBarContainer>
  )
}
